<template>
    <div class="rounded flex flex-col bg-white p-5 @container flex-1 xxs:w-full xxs:min-h-[588px]" :class="isDemo && 'pointer-events-none'">
        <div class="flex flex-row flex-wrap lg:flex-nowrap justify-between mb-3">
            <p class="text-body-3 text-default-1 font-medium">Resources</p>
            <router-link :to="{name: 'resources'}" class="flex flex-row items-center cursor-pointer" v-if="resources.length > 0">
                <p class="text-body-6 font-medium text-primary-1 mr-1.5">View all</p>
                <ArrowRight :color="color.hex"/>
            </router-link>
        </div>
        <div v-if="loading" class="overflow-y-scroll no-scrollbar border-box px-1 flex @xxs:flex-col @lg:flex-row flex-grow mt-6">
            <div class="flex flex-col @xxs:w-full @lg:w-1/2 ml-2">
                <div class="w-full @xxs:h-[90px] @lg:h-[70px] skeleton-loader mt-1" v-for="i in 4" :key="i"/>
            </div>
            <div class="@xxs:hidden @lg:flex flex-col w-1/2 ml-2">
                <div class="w-full h-[70px] skeleton-loader mt-1" v-for="i in 4" :key="i"/>
            </div>
        </div>
        <div v-else class="flex flex-col flex-grow">
            <div v-if="resources.length === 0" class="flex flex-col flex-grow items-center justify-center py-10 box-border">
                <IconResources :color="color.hex" class="mb-6"/>
                <p class="text-body-5 text-default-1">
                    Here you will see all the resources available to you
                </p>
            </div>
            <div v-else class="flex flex-col flex-grow overflow-hidden max-h-[450px]">
                <div class="@lg:grid lg:grid-cols-2 @lg:gap-2 overflow-y-scroll no-scrollbar">
                    <ResourceCard
                        v-for="(resource, index) in resources"
                        :key="resource.uid"
                        :resource="resource"
                        :color="color.hex"
                        class="xxs:w-full box-border @xxs:mt-1 @lg:mt-0"
                        @toggle-like="toggleLike"
                        :index="index"
                    />
                </div>
            </div>
        </div>
        <CoreButton
            v-if="isAdmin || isContentAdmin || isRepresentative"
            buttonType="primary"
            label="Create resource"
            @click="router.push({name: 'create-resources'})"
        />
    </div>
</template>
<script setup>
import CoreButton from "../core/Button.vue";
import IconResources from "../icons/Resources.vue";
import ArrowRight from "../icons/navigation/ArrowRight.vue";
import ResourceCard from "../cards/dashboard/ResourceCard.vue";

import {useTheme} from "@/stores/ThemeStore";
import {useResources} from "@/composables/useResources";
import {useAuth} from "@/stores/AuthStore";
import {computed, onMounted, ref} from "vue";
import {useWidgetDemoData} from "@/composables/useWidgetDemoData";
import router from "@/router";
import {useDomPurifier} from "@/composables/useDomPurifier";

const props = defineProps({
    isDemo: {
        type: Boolean,
        default: false
    }
});

const {resourcesDemoData: demoData} = useWidgetDemoData();

const {getAllResources} = useResources();
const {color} = useTheme();
const {organization} = useAuth();
const {isAdmin, isContentAdmin, isRepresentative} = useAuth();

const data = ref([]);
const meta = ref({});
const links = ref({});
const loading = ref(false);
const hasResources = ref(false);

function toggleLike(index) {
    data.value[index].isLiked = !data.value[index].isLiked;
}

const resources = computed(() => {
    return props.isDemo ? demoData : data.value;
})

onMounted(async () => {
    if(props.isDemo) {
        return;
    }
    loading.value = true;

    const response = await getAllResources(organization.slug, 1, 8);
    data.value = response.data;

    data.value = data.value.map(resource => {
        resource.resourceable.title = useDomPurifier(resource.resourceable.title);
        return resource;
    });

    meta.value = response.meta;
    links.value = response.links;

    loading.value = false;

    if (data.value.length > 0) {
        hasResources.value = true;
    }
})
</script>
